import React, { useState, useEffect, useRef } from "react";
import { Component, DataCardProperties } from "@/types";
import { formatMetricValue } from "@/utils/PropertyFormatters";
import { useDataTextValue } from "@/hooks/useDataTextValue";
import { normalizeLegacySemanticStyleColor } from "@/utils/theme-aware-legacy-colors";
import * as LucideIcons from "lucide-react";
import { AlertCircle, RefreshCw, Info, ChevronRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { DataCardLoadingSkeleton } from "@/components/common/DataLoadingSkeletons";

interface DataCardProps {
  component: Component;
  isPreview?: boolean;
}

const readStyleString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

const lucideIconExports: Record<string, unknown> = LucideIcons;

const isLucideIcon = (value: unknown): value is LucideIcon => {
  if (typeof value === "function") return true;
  return value !== null && typeof value === "object" && "$$typeof" in value;
};

// Hook to detect compact layout based on actual container width (not window width)
// This ensures correct sizing when viewing mobile viewport in desktop browser
const useCompactLayout = (containerRef: React.RefObject<HTMLDivElement>, compactThreshold: number = 250) => {
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const checkWidth = () => {
      const width = element.offsetWidth;
      setIsCompact(width > 0 && width < compactThreshold);
    };

    // Initial check
    checkWidth();

    // Use ResizeObserver for responsive updates
    const resizeObserver = new ResizeObserver(checkWidth);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [containerRef, compactThreshold]);

  return isCompact;
};

export const DataCard: React.FC<DataCardProps> = ({
  component,
  isPreview = false,
}) => {
  const { properties = {}, style = {}, size } = component;
  const props = properties as Partial<DataCardProperties>;

  // Ref for measuring actual card width
  const cardRef = useRef<HTMLDivElement>(null);

  // Detect compact layout based on actual card width (250px threshold for 2-column grid cards)
  const isCompact = useCompactLayout(cardRef, 250);

  // Extract data properties with defaults (static values only)
  const staticLabel = props.label || "Metric";
  const staticValue = props.value ?? 0;
  const valueFormatter = props.valueFormatter || "number";
  const formattingOptions = {
    timeZone: props.timeZone,
    decimalPlaces: props.decimalPlaces,
    thousandsSeparator: props.thousandsSeparator,
    currencySymbol: props.currencySymbol,
    currencyCode: props.currencyCode,
  };
  const subtitle = props.subtitle || "";
  const trend = props.trend?.value;
  const trendLabel = props.trend?.label || "";

  // DATA TEXT INTEGRATION (Phase 8)
  // Use the hook for data binding when useDataText is enabled
  const {
    formattedValue: dataTextValue,
    label: dataTextLabel,
    isLoading,
    error,
    refetch,
  } = useDataTextValue({
    dataSource: props.valueDataSource,
    componentId: component.id,
    valueField: props.valueField,
    labelField: props.useDynamicLabel ? props.labelField : undefined,
    staticValue,
    staticLabel,
    emptyValueText: props.emptyValueText ?? '--',
    valueFormatter,
    ...formattingOptions,
    enabled: props.useDataText === true,
  });

  // Use data-bound values when useDataText is enabled
  const displayValue = props.useDataText
    ? dataTextValue
    : formatMetricValue(staticValue, valueFormatter, formattingOptions);
  const displayLabel = props.useDataText && props.useDynamicLabel
    ? dataTextLabel
    : staticLabel;

  // Styling properties
  const backgroundColor = normalizeLegacySemanticStyleColor(
    "backgroundColor",
    props.cardBackgroundColor || readStyleString(style.backgroundColor) || "hsl(var(--card))",
    component,
  );
  const borderColor = normalizeLegacySemanticStyleColor(
    "borderColor",
    props.cardBorderColor || readStyleString(style.borderColor) || "hsl(var(--border))",
    component,
  );
  const styleBorder = readStyleString(style.border);
  const border = props.cardBorderColor || !styleBorder
    ? undefined
    : normalizeLegacySemanticStyleColor("border", styleBorder, component);
  const borderWidth = props.cardBorderWidth ?? (style.borderWidth ? Number.parseFloat(String(style.borderWidth)) : 1);
  const borderRadius = props.cardBorderRadius ?? (style.borderRadius ? Number.parseFloat(String(style.borderRadius)) : 12);
  const shadowSize = props.cardShadow || "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)";
  const basePadding = props.cardPadding ?? "16px 20px";
  // Use more compact padding on mobile
  const padding = isCompact ? "12px 10px" : basePadding;
  // Extract numeric padding for positioning calculations (use first value from padding string)
  const paddingNum = typeof padding === 'string' ? Number.parseFloat(padding) || 16 : padding;

  // Typography properties
  const labelColor = normalizeLegacySemanticStyleColor(
    "color",
    props.labelColor || "hsl(var(--muted-foreground))",
    component,
  );
  const labelFontSize = props.labelFontSize || "clamp(10px, 3vw, 12px)";
  const labelFontWeight = props.labelFontWeight || 600;
  const labelTextTransform: React.CSSProperties["textTransform"] = "uppercase"; // Fixed value, not in interface
  const labelLetterSpacing = props.labelLetterSpacing || "0.05em";

  const valueColor = normalizeLegacySemanticStyleColor(
    "color",
    props.valueColor || "hsl(var(--foreground))",
    component,
  );
  const valueFontSize = props.valueFontSize || "clamp(28px, 8vw, 36px)";
  const valueFontWeight = props.valueFontWeight || 700;

  const subtitleColor = normalizeLegacySemanticStyleColor(
    "color",
    props.subtitleColor || "hsl(var(--muted-foreground))",
    component,
  );
  const subtitleFontSize = props.subtitleFontSize || "clamp(11px, 3vw, 13px)";
  const subtitleFontWeight = props.subtitleFontWeight || 400;

  const trendFontSize = props.trendFontSize || "clamp(11px, 3vw, 13px)";
  const trendFontWeight = 500; // Fixed value, not in interface

  // Layout properties - tighter gaps on mobile
  const layout = props.layout || "vertical";
  const baseGap = props.gap ?? 8;
  const gap = isCompact ? Math.min(baseGap, 4) : baseGap;
  const alignItems = props.alignItems || "flex-start";
  const justifyContent = props.justifyContent || "flex-start";

  // Icon properties - smaller on mobile for compact layout
  const icon = props.icon || "";
  const iconPosition = props.iconPosition || "right"; // Default to right for etched style
  const baseIconSize = props.iconSize ?? 48; // Larger default for etched appearance
  const iconSize = isCompact ? Math.min(baseIconSize, 28) : baseIconSize;
  const iconColor = normalizeLegacySemanticStyleColor(
    "color",
    props.iconColor || "hsl(var(--muted-foreground))",
    component,
  ); // Muted/etched color by default
  const iconBackgroundColor = props.iconBackgroundColor || "transparent"; // No background by default
  const baseIconBackgroundSize = props.iconBackgroundSize ?? 48;
  const iconBackgroundSize = isCompact ? Math.min(baseIconBackgroundSize, 28) : baseIconBackgroundSize;
  const showIconBackground = props.showIconBackground ?? false; // No background for etched style

  // Corner action icon properties
  const showInfoIcon = props.showInfoIcon ?? false;
  const baseInfoIconSize = props.infoIconSize ?? 14;
  const infoIconSize = isCompact ? Math.min(baseInfoIconSize, 12) : baseInfoIconSize;
  const infoIconColor = normalizeLegacySemanticStyleColor(
    "color",
    props.infoIconColor || "hsl(var(--muted-foreground))",
    component,
  );

  const showNavigationIcon = props.showNavigationIcon ?? false;
  const baseNavIconSize = props.navigationIconSize ?? 16;
  const navigationIconSize = isCompact ? Math.min(baseNavIconSize, 14) : baseNavIconSize;
  const navigationIconColor = normalizeLegacySemanticStyleColor(
    "color",
    props.navigationIconColor || "hsl(var(--muted-foreground))",
    component,
  );

  // Default shadow if not specified
  const boxShadow = shadowSize;

  // Helper: Get trend color
  const getTrendColor = (): string => {
    if (trend === undefined || trend === null) return subtitleColor;
    if (trend > 0) return "#10B981"; // green
    if (trend < 0) return "#EF4444"; // red
    return "hsl(var(--muted-foreground))"; // neutral gray
  };

  // Helper: Get trend icon
  const getTrendIcon = (): string => {
    if (trend === undefined || trend === null) return "";
    if (trend > 0) return "▲";
    if (trend < 0) return "▼";
    return "●";
  };

  // Helper: Render icon
  const renderIcon = () => {
    if (!icon || iconPosition === "none") return null;

    const iconExport = lucideIconExports[icon];
    if (!isLucideIcon(iconExport)) return null;
    const IconComponent = iconExport;

    const iconElement = (
      <IconComponent
        size={iconSize}
        color={iconColor}
        strokeWidth={2}
        aria-hidden="true"
      />
    );

    if (showIconBackground) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: `${iconBackgroundSize}px`,
            height: `${iconBackgroundSize}px`,
            borderRadius: "50%",
            backgroundColor: iconBackgroundColor,
            flexShrink: 0,
          }}
        >
          {iconElement}
        </div>
      );
    }

    return iconElement;
  };

  // Helper: Render info icon (top-right corner)
  const renderInfoIcon = () => {
    if (!showInfoIcon) return null;

    return (
      <div
        style={{
          position: "absolute",
          top: `${paddingNum}px`,
          right: `${paddingNum}px`,
          cursor: "pointer",
          opacity: 0.7,
          transition: "opacity 0.2s ease",
        }}
        data-testid="data-card-info-icon"
        aria-label="More information"
        role="button"
        tabIndex={0}
      >
        <Info
          size={infoIconSize}
          color={infoIconColor}
          strokeWidth={2}
        />
      </div>
    );
  };

  // Helper: Render navigation icon (bottom-right corner)
  const renderNavigationIcon = () => {
    if (!showNavigationIcon) return null;

    return (
      <div
        style={{
          position: "absolute",
          bottom: `${paddingNum}px`,
          right: `${paddingNum}px`,
          cursor: "pointer",
          opacity: 0.6,
          transition: "opacity 0.2s ease",
        }}
        data-testid="data-card-nav-icon"
        aria-label="View details"
        role="button"
        tabIndex={0}
      >
        <ChevronRight
          size={navigationIconSize}
          color={navigationIconColor}
          strokeWidth={2}
        />
      </div>
    );
  };

  // Helper: Render loading state
  const renderLoadingState = () => {
    if (!isLoading || props.showLoadingState === false) return null;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          width: "100%",
          minHeight: "60px",
        }}
        data-testid="data-card-loading"
      >
        <DataCardLoadingSkeleton />
      </div>
    );
  };

  // Helper: Render error state
  const renderErrorState = () => {
    if (!error || props.showErrorState === false) return null;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          width: "100%",
          minHeight: "60px",
          textAlign: "center",
        }}
        data-testid="data-card-error"
      >
        <AlertCircle size={20} color="#EF4444" />
        <span style={{ color: "#EF4444", fontSize: "12px" }}>
          Failed to load data
        </span>
        <button
          onClick={() => refetch()}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            padding: "4px 8px",
            fontSize: "11px",
            color: labelColor,
            border: `1px solid ${borderColor}`,
            borderRadius: "4px",
            backgroundColor: "transparent",
            cursor: "pointer",
          }}
        >
          <RefreshCw size={12} />
          Retry
        </button>
      </div>
    );
  };

  // Helper: Render main content (label, value, subtitle, trend)
  const renderContent = () => {
    // Show loading state when data is being fetched
    if (props.useDataText && isLoading && props.showLoadingState !== false) {
      return renderLoadingState();
    }

    // Show error state when data fetch failed
    if (props.useDataText && error && props.showErrorState !== false) {
      return renderErrorState();
    }

    const trendColor = getTrendColor();
    const trendIcon = getTrendIcon();

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: `${gap}px`,
          flex: 1,
          minWidth: 0, // Allow text truncation
        }}
      >
        {/* Label */}
        <div
          style={{
            color: labelColor,
            fontSize: labelFontSize,
            fontWeight: labelFontWeight,
            textTransform: labelTextTransform,
            letterSpacing: labelLetterSpacing,
            lineHeight: 1.2,
            whiteSpace: "nowrap",
          }}
        >
          {displayLabel}
        </div>

        {/* Value row with optional trend */}
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              color: valueColor,
              fontSize: valueFontSize,
              fontWeight: valueFontWeight,
              lineHeight: 1.1,
            }}
          >
            {displayValue}
          </div>

          {/* Trend indicator */}
          {trend !== undefined && trend !== null && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                color: trendColor,
                fontSize: trendFontSize,
                fontWeight: trendFontWeight,
              }}
            >
              <span aria-hidden="true">{trendIcon}</span>
              <span>
                {Math.abs(trend)}%
                {trendLabel && ` ${trendLabel}`}
              </span>
            </div>
          )}
        </div>

        {/* Subtitle */}
        {subtitle && (
          <div
            style={{
              color: subtitleColor,
              fontSize: subtitleFontSize,
              fontWeight: subtitleFontWeight,
              lineHeight: 1.4,
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
    );
  };

  // Helper to format dimension (handles numbers and string values like "100%")
  const formatDimension = (value: string | number | undefined, fallback: string): string => {
    if (value === undefined || value === null) return fallback;
    if (typeof value === "string") return value;
    return `${value}px`;
  };

  // Determine card wrapper styles
  // Strip width-related properties from component.style - sizing is handled by ComponentWrapper/FlexContainer
  const {
    width: _w,
    minWidth: _mw,
    maxWidth: _xw,
    background: _styleBackground,
    backgroundColor: _styleBackgroundColor,
    borderColor: _styleBorderColor,
    border: _styleBorder,
    color: _styleColor,
    ...visualStyle
  } = (style || {}) as React.CSSProperties;
  const cardStyle: React.CSSProperties = {
    ...visualStyle,
    display: "flex",
    position: "relative",
    backgroundColor,
    border: border ?? (borderWidth > 0 ? `${borderWidth}px solid ${borderColor}` : "none"),
    borderRadius: `${borderRadius}px`,
    boxShadow,
    padding,
    width: formatDimension(size?.width, "100%"),
    height: formatDimension(size?.height, "auto"),
    overflow: "hidden",
    minWidth: 0, // Allow flex item to shrink below content size
  };

  // Icon positioning logic
  if (iconPosition === "right") {
    // Icon in absolute right side, vertically centered
    return (
      <div ref={cardRef} style={cardStyle} data-testid="data-card">
        {/* Info icon (top-right corner) */}
        {renderInfoIcon()}

        {/* Main content (left) */}
        <div
          style={{
            display: "flex",
            flexDirection: layout === "vertical" ? "column" : "row",
            alignItems,
            justifyContent,
            gap: `${gap}px`,
            flex: 1,
            minWidth: 0, // Allow content to shrink for proper flex wrapping
            paddingRight: icon ? `${iconSize + (isCompact ? 8 : 16)}px` : "0", // Space for icon, tighter on mobile
          }}
        >
          {renderContent()}
        </div>

        {/* Icon (absolute positioned right, vertically centered) */}
        {icon && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              right: `${paddingNum}px`,
              transform: "translateY(-50%)",
              opacity: 0.6, // Subtle etched appearance
            }}
          >
            {renderIcon()}
          </div>
        )}

        {/* Navigation icon (bottom-right corner) */}
        {renderNavigationIcon()}
      </div>
    );
  }

  // Icon positions: left, top, none (inline flow)
  const flexDirection =
    iconPosition === "top" || layout === "vertical" ? "column" : "row";

  return (
    <div ref={cardRef} style={cardStyle} data-testid="data-card">
      {/* Info icon (top-right corner) */}
      {renderInfoIcon()}

      <div
        style={{
          display: "flex",
          flexDirection,
          alignItems,
          justifyContent,
          gap: `${gap}px`,
          width: "100%",
        }}
      >
        {/* Icon before content (for left/top positions) */}
        {(iconPosition === "left" || iconPosition === "top") && renderIcon()}

        {/* Main content */}
        {renderContent()}
      </div>

      {/* Navigation icon (bottom-right corner) */}
      {renderNavigationIcon()}
    </div>
  );
};

export default DataCard;
